import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import { UUID } from "crypto";
import {
  useWhatsappChatMutations,
  useWhatsappMessageMutations,
} from "@stockline/web";
import { useUser } from "@/components/auth-provider";
import api from "@/app/api";
import { whatsappKeys } from "../keys/whatsapp";
import { WhatsAppMessageTypeEnum } from "@/app/api/types/whatsapp";
import type {
  WhatsAppInstanceResponse,
  WhatsAppInstanceCreate,
  WhatsAppChatResponse,
  WhatsAppChatList,
} from "@/app/api/types/whatsapp";
import type { PageOptions } from "@/app/types";
import { useBulkMarkAsReadMutation } from "./_dexieRollback";
import type { Query } from "@tanstack/react-query";

const DELTA_BUFFER_MS = 5_000;
const DELTA_INTERVAL_MS = 30_000;

/** Predicate to match only chatsList queries under whatsappKeys.chats(), NOT message/chat detail queries */
const isChatListQuery = (query: Query) =>
  Array.isArray(query.queryKey) && query.queryKey[2] === "list";

/** Invalidate all chat list variants (with any filter combination) */
function invalidateAllChatLists(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({
    queryKey: whatsappKeys.chats(),
    predicate: isChatListQuery,
  });
}

// Instance queries
export function useWhatsAppInstances() {
  return useQuery({
    queryKey: whatsappKeys.instances(),
    queryFn: () => api.whatsapp.getInstances(),
  });
}

export function useRefreshWhatsAppInstance(instanceId: string | null) {
  return useQuery({
    queryKey: whatsappKeys.instance(instanceId!),
    queryFn: () => api.whatsapp.refreshInstance(instanceId!),
    enabled: !!instanceId,
    refetchInterval: () => (document.hidden ? false : 60_000),
  });
}

/**
 * Hook to fetch WhatsApp chats with delta polling.
 *
 * - First fetch: full (no modified_since)
 * - Subsequent: delta only (modified_since = last server_time - 5s buffer)
 * - 0 items returned → same ref → no rerender
 * - Also picks up chats with new AI-created pre-orders
 */
export function useWhatsAppChats(options?: {
  search?: string;
  includeArchived?: boolean;
  archivedOnly?: boolean;
}) {
  const serverTimeRef = useRef<string | null>(null);
  const cachedResultRef = useRef<WhatsAppChatList | null>(null);
  const cachedCountsRef = useRef<Record<string, number>>({});

  const queryFingerprint = `${options?.search}|${options?.includeArchived}|${options?.archivedOnly}`;
  const prevFingerprintRef = useRef(queryFingerprint);

  const fetchWithDelta = useCallback(async (): Promise<WhatsAppChatList> => {
    // Reset on fingerprint change (search, archive toggle)
    if (prevFingerprintRef.current !== queryFingerprint) {
      serverTimeRef.current = null;
      cachedResultRef.current = null;
      cachedCountsRef.current = {};
      prevFingerprintRef.current = queryFingerprint;
    }

    let modifiedSince: string | undefined;
    if (serverTimeRef.current) {
      const since = new Date(
        new Date(serverTimeRef.current).getTime() - DELTA_BUFFER_MS,
      );
      modifiedSince = since.toISOString().slice(0, -1);
    }

    const result = await api.whatsapp.getAllUserDbChats({
      search: options?.search,
      includeArchived: options?.includeArchived,
      archivedOnly: options?.archivedOnly,
      modifiedSince,
    });

    serverTimeRef.current = result.server_time ?? serverTimeRef.current;

    let mergedResult: WhatsAppChatList;

    if (modifiedSince && cachedResultRef.current) {
      if (result.chats.length === 0) {
        // Nothing changed → return same reference → no rerender
        return cachedResultRef.current;
      }
      // Merge changed items into cache (WhatsApp uses uid as identifier)
      const changedIds = new Set(result.chats.map((c) => c.uid));
      const kept = cachedResultRef.current.chats.filter(
        (c) => !changedIds.has(c.uid),
      );
      const mergedChats = [...kept, ...result.chats].sort(
        (a, b) =>
          new Date(b.last_message_date ?? 0).getTime() -
          new Date(a.last_message_date ?? 0).getTime(),
      );
      // Overlay pre-order counts
      const mergedCounts = { ...cachedCountsRef.current };
      if (result.unread_preorder_counts) {
        Object.assign(mergedCounts, result.unread_preorder_counts);
      }
      mergedResult = {
        ...cachedResultRef.current,
        chats: mergedChats,
        total: cachedResultRef.current.total,
        unread_preorder_counts: mergedCounts,
      };
      cachedCountsRef.current = mergedCounts;
    } else {
      // Full fetch (first load or fingerprint change)
      mergedResult = result;
      cachedCountsRef.current = result.unread_preorder_counts ?? {};
    }

    cachedResultRef.current = mergedResult;

    // Per-chat unread counts are now seeded by usePreOrderStatusCounts.
    // The legacy /api/whatsapp/chats response still bundles them but we
    // ignore that field — the V2 source filters soft-deleted pre-orders and
    // shares one cache for all 3 channels.

    return mergedResult;
  }, [
    queryFingerprint,
    options?.search,
    options?.includeArchived,
    options?.archivedOnly,
  ]);

  return useQuery({
    queryKey: whatsappKeys.chatsList(
      options?.search,
      options?.includeArchived,
      options?.archivedOnly,
    ),
    queryFn: fetchWithDelta,
    staleTime: DELTA_INTERVAL_MS,
    refetchInterval: () => (document.hidden ? 5 * 60_000 : DELTA_INTERVAL_MS),
    refetchIntervalInBackground: true,
  });
}

export function useWhatsAppUnreadChats() {
  return useQuery({
    queryKey: whatsappKeys.unreadChats(),
    queryFn: () => api.whatsapp.getChatsWithUnread(),
  });
}

// Chat detail query
export function useWhatsAppChat(chatId: UUID | null) {
  return useQuery({
    queryKey: whatsappKeys.chat(chatId!),
    queryFn: async () => {
      const result = await api.whatsapp.getAllUserDbChats();
      return result.chats.find((c) => c.uid === chatId);
    },
    enabled: !!chatId,
  });
}

// Legacy single-page message fetch — kept only for the frozen `recap/mobile/_legacy`
// surface (mobile recap pre-rewrite). New code MUST use the offline-first
// `useWhatsappMessagesPaged` hook from `@stockline/web`.
export function useWhatsAppMessages(
  chatId: UUID | null,
  pageOptions: PageOptions,
) {
  return useQuery({
    queryKey: [...whatsappKeys.messages(chatId!), pageOptions],
    queryFn: () => api.whatsapp.getChatMessages(chatId!, pageOptions),
    enabled: !!chatId,
    staleTime: 1000 * 60 * 2,
  });
}

// Instance mutations
export function useCreateWhatsAppInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: WhatsAppInstanceCreate) =>
      api.whatsapp.createInstance(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappKeys.instances() });
    },
  });
}

export function useDeleteWhatsAppInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (instanceId: string) => api.whatsapp.deleteInstance(instanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappKeys.instances() });
    },
  });
}

export function useConnectWhatsAppInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (instanceId: string) =>
      api.whatsapp.connectInstance(instanceId),
    onSuccess: (_, instanceId) => {
      queryClient.invalidateQueries({
        queryKey: whatsappKeys.instance(instanceId),
      });
    },
  });
}

// Chat mutations
//
// Send paths write optimistically into Dexie (`whatsappMessages` table) —
// the same store `useWhatsappMessagesPaged` reads — so the bubble appears
// the instant the user hits Send. The legacy REST call still kicks off
// real delivery via Evolution; once Evolution echoes back, V2 SSE fans the
// real row into Dexie and `reconcileWhenRealArrives` deletes the temp.
export function useSendWhatsAppMessage() {
  const queryClient = useQueryClient();
  const { injectOptimistic, removeOptimistic, reconcileWhenRealArrives } =
    useWhatsappMessageMutations();
  const user = useUser();

  return useMutation({
    mutationFn: ({ chatId, text }: { chatId: UUID; text: string }) =>
      api.whatsapp.sendMessage(chatId, text),
    onMutate: async ({ chatId, text }) => {
      const tempId = await injectOptimistic({
        companyId: user.company_id ?? "",
        userId: user.id,
        chatUid: String(chatId),
        content: { contentType: "text", text },
        messageType: WhatsAppMessageTypeEnum.CONVERSATION,
      });
      return { tempId };
    },
    onError: (_err, _variables, context) => {
      if (context?.tempId) {
        void removeOptimistic(context.tempId);
      }
    },
    onSuccess: (response, _variables, context) => {
      // Surface the new message in the chat-list preview row.
      invalidateAllChatLists(queryClient);

      // Evolution returned the WhatsApp key.id — use it to detect when the
      // real DB row arrives via SSE and drop the optimistic on match.
      const realKeyId = (response.data as { key?: { id?: string } } | null)?.key
        ?.id;
      if (context?.tempId && realKeyId) {
        reconcileWhenRealArrives(context.tempId, realKeyId);
      } else if (context?.tempId) {
        // No key returned (shouldn't happen on success, but be safe). Drop
        // the optimistic shortly so we don't leave a ghost row.
        setTimeout(() => void removeOptimistic(context.tempId), 8_000);
      }
    },
    // Don't retry on failure - just fail fast
    retry: false,
  });
}

export function useSendWhatsAppReaction() {
  const { injectOptimistic, removeOptimistic, reconcileWhenRealArrives } =
    useWhatsappMessageMutations();
  const user = useUser();

  return useMutation({
    mutationFn: ({
      chatId,
      messageKeyId,
      emoji,
    }: {
      chatId: UUID;
      messageKeyId: string;
      emoji: string;
    }) => api.whatsapp.sendReaction(chatId, messageKeyId, emoji),

    onMutate: async ({ chatId, messageKeyId, emoji }) => {
      const tempId = await injectOptimistic({
        companyId: user.company_id ?? "",
        userId: user.id,
        chatUid: String(chatId),
        content: {
          contentType: "reaction",
          reacted_message_key_id: messageKeyId,
          text: emoji,
          sender_timestamp_ms: String(Date.now()),
        },
        messageType: WhatsAppMessageTypeEnum.REACTION_MESSAGE,
      });
      return { tempId };
    },

    onError: (_err, _variables, context) => {
      if (context?.tempId) {
        void removeOptimistic(context.tempId);
      }
    },

    onSuccess: (response, _variables, context) => {
      const realKeyId = (response.data as { key?: { id?: string } } | null)?.key
        ?.id;
      if (context?.tempId && realKeyId) {
        reconcileWhenRealArrives(context.tempId, realKeyId);
      } else if (context?.tempId) {
        setTimeout(() => void removeOptimistic(context.tempId), 8_000);
      }
    },

    retry: false,
  });
}

export function useMarkWhatsAppChatAsRead() {
  const queryClient = useQueryClient();
  // Chat list UI reads `unreadCount` from the Dexie offline store (V2 sync),
  // not from this TanStack cache. Without an optimistic Dexie write the
  // unread badge stays visible until SSE round-trips. The TanStack writes
  // below stay so any legacy callers still see the optimistic value.
  const { markAsReadOptimistic } = useWhatsappChatMutations();

  return useMutation({
    mutationFn: (chatId: UUID) => api.whatsapp.markChatAsRead(chatId),
    onMutate: async (chatId: UUID) => {
      // Only cancel chat list queries — never cancel in-flight message fetches
      await queryClient.cancelQueries({
        queryKey: whatsappKeys.chats(),
        predicate: isChatListQuery,
      });

      const previousQueries = queryClient.getQueriesData<WhatsAppChatList>({
        queryKey: whatsappKeys.chats(),
        predicate: isChatListQuery,
      });

      queryClient.setQueriesData<WhatsAppChatList>(
        { queryKey: whatsappKeys.chats(), predicate: isChatListQuery },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            chats: old.chats.map((chat) =>
              chat.uid === chatId ? { ...chat, unread_count: 0 } : chat,
            ),
          };
        },
      );

      // If Dexie throws (quota exceeded, transaction abort, etc.) we still
      // need to return the TanStack rollback context so onError can revert
      // the cache. Treat a missing Dexie rollback as a no-op.
      let rollbackDexie: (() => Promise<void>) | null = null;
      try {
        rollbackDexie = await markAsReadOptimistic(chatId);
      } catch {
        rollbackDexie = null;
      }

      return { previousQueries, rollbackDexie };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      void context?.rollbackDexie?.();
    },
    onSuccess: (updatedChat) => {
      queryClient.setQueryData(
        whatsappKeys.chat(updatedChat.uid as UUID),
        updatedChat,
      );
      queryClient.invalidateQueries({ queryKey: whatsappKeys.unreadChats() });
    },
  });
}

export function useMarkAllWhatsAppChatsAsRead() {
  // Dexie bulk-flip clears the unread badges instantly; the legacy
  // TanStack writes below stay for any non-Dexie callers, and SSE per-
  // row updates reconcile `syncId` after.
  const queryClient = useQueryClient();
  const { markAllAsReadOptimistic } = useWhatsappChatMutations();
  return useBulkMarkAsReadMutation<WhatsAppChatList>({
    mutationFn: () => api.whatsapp.markAllChatsAsRead(),
    queryKey: whatsappKeys.chats(),
    predicate: isChatListQuery,
    setUnreadZero: (old) => ({
      ...old,
      chats: old.chats.map((chat) => ({ ...chat, unread_count: 0 })),
    }),
    optimistic: markAllAsReadOptimistic,
    logPrefix: "useMarkAllWhatsAppChatsAsRead",
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappKeys.unreadChats() });
    },
  });
}

export function useLinkPartnerToWhatsAppChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, partnerId }: { chatId: UUID; partnerId: UUID }) =>
      api.whatsapp.linkPartnerToChat(chatId, partnerId),
    onSuccess: (updatedChat) => {
      queryClient.setQueryData(
        whatsappKeys.chat(updatedChat.uid as UUID),
        updatedChat,
      );
      invalidateAllChatLists(queryClient);
    },
  });
}

export function useUnlinkPartnerFromWhatsAppChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: UUID) => api.whatsapp.unlinkPartnerFromChat(chatId),
    onSuccess: (updatedChat) => {
      queryClient.setQueryData(
        whatsappKeys.chat(updatedChat.uid as UUID),
        updatedChat,
      );
      invalidateAllChatLists(queryClient);
    },
  });
}

export function useUpdateWhatsAppChatName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chatId, name }: { chatId: UUID; name: string }) =>
      api.whatsapp.patchChatsUidName(chatId, { name }),
    onSuccess: (updatedChat) => {
      queryClient.setQueryData(
        whatsappKeys.chat(updatedChat.uid as UUID),
        updatedChat,
      );
      invalidateAllChatLists(queryClient);
    },
  });
}

export function useDeleteWhatsAppChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: UUID) => api.whatsapp.deleteChat(chatId),
    onSuccess: () => {
      invalidateAllChatLists(queryClient);
    },
  });
}

// Archive mutations
//
// The chat list reads from Dexie via `useOfflineWhatsappChats()` and
// filters on `archived`/`enabled` locally. The TanStack cache here is
// consulted only by legacy surfaces. Without an optimistic Dexie write the
// UI freezes on archive/disable until the SSE round-trip lands. See the
// equivalent block in `useSMS.ts` for the full rationale.
export function useArchiveWhatsAppChat() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useWhatsappChatMutations();

  return useMutation({
    mutationFn: (chatId: UUID) => api.whatsapp.archiveChat(chatId),
    onMutate: async (chatId: UUID) => {
      const rollback = await setArchivedOptimistic(String(chatId), true);

      // Only cancel chat list queries — never cancel in-flight message fetches
      await queryClient.cancelQueries({
        queryKey: whatsappKeys.chats(),
        predicate: isChatListQuery,
      });

      const previousQueries = queryClient.getQueriesData<WhatsAppChatList>({
        queryKey: whatsappKeys.chats(),
        predicate: isChatListQuery,
      });

      queryClient.setQueriesData<WhatsAppChatList>(
        { queryKey: whatsappKeys.chats(), predicate: isChatListQuery },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            chats: old.chats.filter((chat) => chat.uid !== chatId),
          };
        },
      );

      return { previousQueries, rollback };
    },
    onError: (_err, _chatId, context) => {
      if (context?.rollback) void context.rollback();
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

export function useUnarchiveWhatsAppChat() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useWhatsappChatMutations();

  return useMutation({
    mutationFn: (chatId: UUID) => api.whatsapp.unarchiveChat(chatId),
    onMutate: async (chatId: UUID) => {
      const rollback = await setArchivedOptimistic(String(chatId), false);

      // Only cancel chat list queries — never cancel in-flight message fetches
      await queryClient.cancelQueries({
        queryKey: whatsappKeys.chats(),
        predicate: isChatListQuery,
      });

      const previousQueries = queryClient.getQueriesData<WhatsAppChatList>({
        queryKey: whatsappKeys.chats(),
        predicate: isChatListQuery,
      });

      queryClient.setQueriesData<WhatsAppChatList>(
        { queryKey: whatsappKeys.chats(), predicate: isChatListQuery },
        (old) => {
          if (!old) return old;
          return {
            ...old,
            chats: old.chats.filter((chat) => chat.uid !== chatId),
          };
        },
      );

      return { previousQueries, rollback };
    },
    onError: (_err, _chatId, context) => {
      if (context?.rollback) void context.rollback();
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
  });
}

export function useArchiveWhatsAppChatsBatch() {
  const queryClient = useQueryClient();
  const { setArchivedOptimistic } = useWhatsappChatMutations();

  return useMutation({
    mutationFn: (chatUids: string[]) =>
      api.whatsapp.archiveChatsBatch(chatUids),
    onMutate: async (chatUids: string[]) => {
      const rollbacks = await Promise.all(
        chatUids.map((id) => setArchivedOptimistic(id, true)),
      );
      return { rollbacks };
    },
    onError: (_err, _chatUids, context) => {
      if (context?.rollbacks) {
        for (const rollback of context.rollbacks) {
          if (rollback) void rollback();
        }
      }
    },
    onSuccess: () => {
      // Invalidate all chat lists
      invalidateAllChatLists(queryClient);
    },
  });
}

export function useFetchAvailableWhatsAppChats() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (instanceId: string) =>
      api.whatsapp.fetchAvailableChats(instanceId),
    onSuccess: () => {
      invalidateAllChatLists(queryClient);
    },
  });
}

export function useImportWhatsAppChats() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      instanceId,
      chats,
    }: {
      instanceId: string;
      chats: WhatsAppChatResponse[];
    }) => api.whatsapp.importChats(instanceId, chats),
    onSuccess: () => {
      invalidateAllChatLists(queryClient);
    },
  });
}

/**
 * Hook to disable a WhatsApp chat (hide from main chat list but keep storing messages)
 */
export function useDisableWhatsAppChat() {
  const queryClient = useQueryClient();
  const { setEnabledOptimistic } = useWhatsappChatMutations();

  return useMutation({
    mutationFn: (chatUid: string) => api.whatsapp.disableChat(chatUid),
    onMutate: async (chatUid: string) => {
      const rollback = await setEnabledOptimistic(chatUid, false);

      // Only cancel chat list queries — never cancel in-flight message fetches
      await queryClient.cancelQueries({
        queryKey: whatsappKeys.chats(),
        predicate: isChatListQuery,
      });

      const previousQueries = queryClient.getQueriesData<WhatsAppChatList>({
        queryKey: whatsappKeys.chats(),
        predicate: isChatListQuery,
      });

      queryClient.setQueriesData<WhatsAppChatList>(
        { queryKey: whatsappKeys.chats(), predicate: isChatListQuery },
        (old) => {
          if (!old || !old.chats) return old;
          return {
            ...old,
            chats: old.chats.filter((chat) => chat.uid !== chatUid),
          };
        },
      );

      return { previousQueries, rollback };
    },
    onError: (_err, _chatUid, context) => {
      if (context?.rollback) void context.rollback();
      if (context?.previousQueries) {
        context.previousQueries.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: (_data, chatUid) => {
      // Remove the disabled chat's message cache
      queryClient.removeQueries({
        queryKey: whatsappKeys.messages(chatUid as UUID),
      });
      // Invalidate chat list so the disabled chat is removed from cache
      queryClient.invalidateQueries({
        queryKey: whatsappKeys.chats(),
      });
    },
  });
}
